// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import type {RoomWalk, VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import {makeAutoObservable} from 'mobx';

interface ShownRoom {
	readonly handle: VoiceSceneHandle;
	readonly identity: string;
}

// The 3D room on screen with characters on, and my own participant identity in it: what the eyes shortcut and the
// toolbar button act on. The room registers while it shows and unregisters when it goes; so does my walk there, while
// walking is on, for the follow shortcut.
class VoiceSceneCamera {
	room: ShownRoom | null = null;
	walk: RoomWalk | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true, deep: false});
		ResettableStates.register(this, this.reset);
	}

	reset(): void {
		this.room = null;
		this.walk = null;
	}

	get available(): boolean {
		return this.room !== null;
	}

	register(handle: VoiceSceneHandle, identity: string): () => void {
		const room = {handle, identity};
		this.room = room;
		return () => {
			if (this.room === room) this.room = null;
		};
	}

	get canFollow(): boolean {
		return this.room !== null && this.walk !== null;
	}

	registerWalk(walk: RoomWalk): () => void {
		this.walk = walk;
		return () => {
			if (this.walk === walk) this.walk = null;
		};
	}

	// Behind my walking character (standing it up first), or back to the room view; false without a walk on screen.
	toggleFollow(): boolean {
		if (!this.canFollow) return false;
		this.walk!.toggleFollow();
		return true;
	}

	// In eyes: back to the orbit; otherwise into my own character's eyes. false when nothing changed.
	toggleEyes(): boolean {
		const room = this.room;
		if (!room) return false;
		if (room.handle.getCameraMode() === 'eyes') {
			room.handle.exitToOrbit();
			return true;
		}
		return room.handle.enterEyes?.(room.identity) ?? false;
	}
}

export default new VoiceSceneCamera();
